import { QueryClient } from '@tanstack/react-query';
import {
  AssetCollection,
  AssetDto,
  AssetType,
  PortfolioDto,
  SectionAssetDto,
  TransactionDto,
  TransactionStatus,
  TransactionType
} from '@unistocks/proxy/axios-client';
import {
  portfoliosGetListQueryKey,
  portfoliosGetQueryKey,
  portfoliosGetTimeSeriesQueryKey
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { PortfolioFilter, includeAllFilter } from '@unistocks/shared/filter';
import { isNumber } from '@unistocks/shared/utils/util';
import { Filter } from '@unistocks/types';
import { isAccount, isHoldings, isLoan } from '../../shared/utils/asset-utils';
import { PortfolioBase } from './models/portfolio-base';
import { PORTFOLIO } from './portfolio-type';

export const getAssetCollection = (
  portfolio: PortfolioBase,
  type?: AssetCollection
): Array<AssetDto> => {
  const assets = portfolio.sections.flatMap(section => section.assets);

  switch (type) {
    case AssetCollection.Listed:
      return assets.filter(asset => asset.isListed);

    case AssetCollection.Unlisted:
      return assets.filter(asset => !asset.isListed);

    case AssetCollection.Holdings:
      return assets.filter(isHoldings);

    case AssetCollection.Cash:
      return assets.filter(isAccount);

    case AssetCollection.Loans:
      return assets.filter(isLoan);

    case AssetCollection.All:
    default:
      return assets;
  }
};

/**
 * Filter assets from a portfolio
 * Defaults to include all
 * @param portfolio
 * @param filter
 * @returns
 */
export const filterAssets = (
  portfolio: PortfolioBase,
  { assetTypes, assets, sections, type }: PortfolioFilter = {}
): Array<SectionAssetDto> => {
  // Select assets based on filter
  const assetTypeFilter = assetTypes || (includeAllFilter as Filter<AssetType>);
  const assetFilter = assets || (includeAllFilter as Filter<string>);
  const sectionFilter = sections || (includeAllFilter as Filter<string>);

  const assetList = getAssetCollection(portfolio, type);
  const sectionList = portfolio.sections.filter(section => {
    if (sectionFilter.type === 'exclude') {
      return !sectionFilter.values.includes(section.id);
    }

    return sectionFilter.values.includes(section.id);
  });

  return sectionList
    .flatMap(section => section.assets)
    .filter(asset => {
      if (!assetList.includes(asset)) {
        return false;
      }

      let include: boolean;

      // Filter by asset type
      if (assetTypeFilter.type === 'exclude') {
        include = !assetTypeFilter.values.includes(asset.type);
      } else {
        include = assetTypeFilter.values.includes(asset.type);
      }

      // If asset type is included, filter on asset filter
      if (include) {
        if (assetFilter.type === 'exclude') {
          include = !assetFilter.values.includes(asset.id);
        } else {
          include = assetFilter.values.includes(asset.id);
        }
      }

      return include;
    });
};

export const calculateAssetVaue = (portfolio: PortfolioDto, filter: PortfolioFilter): number => {
  return filterAssets(portfolio, filter).reduce((acc, asset) => acc + asset.calculatedValue, 0);
};

/**
 * Return the net sum of an array of transactions
 * @param {TransactionDto[]} transactions
 * @param {TransactionStatus} status - The status of the transactions to include
 * @returns {number}
 * @memberof UtilService
 */
export function sumTransactions(
  transactions: Array<TransactionDto>,
  status?: TransactionStatus
): number {
  return transactions.reduce((sum: number, transaction: TransactionDto) => {
    if (isNumber(status) && transaction.status !== status) {
      return sum;
    }

    if (transaction.type === TransactionType.Deposit) {
      return sum + transaction.amount.value;
    } else {
      return sum - transaction.amount.value;
    }
  }, 0);
}

export const isPortfolio = (portfolio: PortfolioBase): portfolio is PortfolioDto => {
  return portfolio?.type === PORTFOLIO;
};

export const invalidatePortfolioQueriesById = (queryClient: QueryClient, id: string) => {
  // Invalidate portfolio
  queryClient.invalidateQueries({ queryKey: portfoliosGetQueryKey(id) });
  // Invalidate portfolio list
  queryClient.invalidateQueries({ queryKey: portfoliosGetListQueryKey() });
  // Invalidate portfolio history
  queryClient.invalidateQueries({ queryKey: portfoliosGetTimeSeriesQueryKey(id) });
};
